import { getAuthHeader } from "../actions/utils/api";

export type TelemetryLevel = "debug" | "info" | "warn" | "error";

export interface TelemetryEntry {
  sessionId: string;
  event: string;
  timestamp: string;
  level?: TelemetryLevel;
  jobId?: string;
  userId?: string;
  metadata?: Record<string, unknown>;
}

export interface TelemetryLogOptions {
  jobId?: string;
  level?: TelemetryLevel;
  metadata?: Record<string, unknown>;
}

const ENDPOINT = "/api/telemetry/frontend-log";
const FLUSH_INTERVAL_MS = 5_000;
const BATCH_THRESHOLD = 20;
const MAX_QUEUE_SIZE = 200;
const MAX_BATCH_PER_REQUEST = 50;

const API_BASE_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ||
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
  (import.meta.env.DEV ? "http://localhost:3001" : "");

function resolveEndpoint(): string {
  const base = API_BASE_URL.replace(/\/+$/, "");
  return `${base}${ENDPOINT}`;
}

function generateSessionId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

class TelemetryClient {
  private queue: TelemetryEntry[] = [];
  private readonly sessionId: string;
  private userId: string | null = null;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private isEnabled: boolean;
  private unloadRegistered = false;
  private flushingPromise: Promise<void> | null = null;

  constructor() {
    this.sessionId = generateSessionId();
    const flag = (import.meta.env.VITE_TELEMETRY_ENABLED as string | undefined) ?? "true";
    this.isEnabled = flag !== "false" && flag !== "0";
  }

  enable(): void {
    if (this.isEnabled) return;
    this.isEnabled = true;
    this.ensureStarted();
  }

  disable(): void {
    this.isEnabled = false;
    this.stop();
    this.queue = [];
  }

  setUserId(userId: string | null): void {
    this.userId = userId;
  }

  getSessionId(): string {
    return this.sessionId;
  }

  log(event: string, opts: TelemetryLogOptions = {}): void {
    if (!this.isEnabled) return;
    if (!event) return;

    const entry: TelemetryEntry = {
      sessionId: this.sessionId,
      event,
      timestamp: new Date().toISOString(),
    };
    if (opts.level) entry.level = opts.level;
    if (opts.jobId) entry.jobId = opts.jobId;
    if (this.userId) entry.userId = this.userId;
    if (opts.metadata) entry.metadata = opts.metadata;

    this.queue.push(entry);

    if (this.queue.length > MAX_QUEUE_SIZE) {
      this.queue.splice(0, this.queue.length - MAX_QUEUE_SIZE);
    }

    this.ensureStarted();

    if (this.queue.length >= BATCH_THRESHOLD) {
      void this.flush();
    }
  }

  flush(): Promise<void> {
    if (!this.isEnabled) return Promise.resolve();
    if (this.flushingPromise) return this.flushingPromise;
    if (this.queue.length === 0) return Promise.resolve();

    const entries = this.queue.splice(0, MAX_BATCH_PER_REQUEST);
    this.flushingPromise = this.send(entries, false)
      .catch((error) => {
        this.queue = entries.concat(this.queue).slice(0, MAX_QUEUE_SIZE);
        console.warn("[telemetry] flush failed", error);
      })
      .finally(() => {
        this.flushingPromise = null;
      });
    return this.flushingPromise;
  }

  flushOnUnload(): void {
    if (!this.isEnabled || this.queue.length === 0) return;
    const entries = this.queue.splice(0, MAX_BATCH_PER_REQUEST);
    void this.send(entries, true).catch(() => undefined);
  }

  private async send(entries: TelemetryEntry[], isUnload: boolean): Promise<void> {
    if (entries.length === 0) return;

    const auth = await getAuthHeader();
    if (!auth.Authorization) {
      return;
    }

    const body = JSON.stringify({ entries });
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Authorization: auth.Authorization,
    };

    const url = resolveEndpoint();
    await fetch(url, {
      method: "POST",
      headers,
      body,
      credentials: "include",
      keepalive: isUnload,
    });
  }

  private ensureStarted(): void {
    if (!this.isEnabled) return;
    if (!this.flushTimer && typeof window !== "undefined") {
      this.flushTimer = setInterval(() => {
        void this.flush();
      }, FLUSH_INTERVAL_MS);
    }
    if (!this.unloadRegistered && typeof window !== "undefined") {
      window.addEventListener("pagehide", this.onUnload);
      window.addEventListener("visibilitychange", this.onVisibilityChange);
      this.unloadRegistered = true;
    }
  }

  private stop(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.unloadRegistered && typeof window !== "undefined") {
      window.removeEventListener("pagehide", this.onUnload);
      window.removeEventListener("visibilitychange", this.onVisibilityChange);
      this.unloadRegistered = false;
    }
  }

  private onUnload = (): void => {
    this.flushOnUnload();
  };

  private onVisibilityChange = (): void => {
    const state = typeof document !== "undefined" ? document.visibilityState : "unknown";
    this.log("visibility.change", { metadata: { state } });
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      this.flushOnUnload();
    }
  };
}

export const telemetryClient = new TelemetryClient();
